import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { TierBadge } from '../components/TierBadge';
import { Video, Mic2, Play, Clock, Eye, Heart, Share2, ExternalLink, User, Music, Building2, Camera, Headphones } from 'lucide-react';
import { API } from '../config/api';



export default function InterviewDetailPage() {
  const { interviewId } = useParams();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInterview();
  }, [interviewId]);

  const fetchInterview = async () => {
    try {
      const response = await fetch(`${API}/interviews/${interviewId}`);
      if (response.ok) {
        setInterview(await response.json());
      } else {
        setError('Interview not found');
      }
    } catch (err) {
      setError('Failed to load interview');
    } finally {
      setLoading(false);
    }
  };

  const formatBadgeColor = (format) => {
    switch (format) {
      case 'video': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'audio': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'live': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !interview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-2xl font-bold mb-2">Interview Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/interviews">
            <Button>Back to Interviews</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="interview-detail-page">
      <div className="max-w-6xl mx-auto">
        {/* Video/Audio Player Section */}
        <div className="mb-8">
          <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
            {interview.youtube_url ? (
              <iframe
                src={`https://www.youtube.com/embed/${interview.youtube_url.split('v=')[1]?.split('&')[0] || interview.youtube_url.split('/').pop()}`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={interview.title}
              />
            ) : interview.vimeo_url ? (
              <iframe
                src={`https://player.vimeo.com/video/${interview.vimeo_url.split('/').pop()}`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={interview.title}
              />
            ) : interview.audio_url ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-green-900/50 to-black p-8">
                <Mic2 className="w-24 h-24 text-green-500 mb-6" />
                <audio controls className="w-full max-w-md">
                  <source src={interview.audio_url} type="audio/mpeg" />
                </audio>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Video className="w-24 h-24 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Interview Info */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Meta */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={formatBadgeColor(interview.format)}>
                  {interview.format}
                </Badge>
                {interview.explicit && (
                  <Badge variant="outline" className="text-red-400 border-red-400/30">
                    Explicit
                  </Badge>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4" data-testid="interview-title">
                {interview.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {interview.plays || 0} views
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" /> {interview.likes || 0} likes
                </span>
                {interview.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {interview.duration}
                  </span>
                )}
              </div>
            </div>

            {/* Host & Guest */}
            <Card className="glass-card border-border p-6">
              <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-4">Featuring</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Host */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                    {interview.host_avatar_url ? (
                      <img src={interview.host_avatar_url} alt={interview.host_name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Host</p>
                    <p className="font-bold">{interview.host_name}</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {interview.host_type}
                    </Badge>
                  </div>
                </div>
                
                {/* Guest */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center overflow-hidden">
                    {interview.guest_avatar_url ? (
                      <img src={interview.guest_avatar_url} alt={interview.guest_name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-secondary" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Guest</p>
                    <p className="font-bold">{interview.guest_name}</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {interview.guest_type}
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>

            {/* Description */}
            {interview.description && (
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">About</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{interview.description}</p>
              </Card>
            )}

            {/* Linked Tracks */}
            {interview.linked_tracks && interview.linked_tracks.length > 0 && (
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-4 flex items-center gap-2">
                  <Music className="w-4 h-4" /> Music Discussed
                </h3>
                <div className="space-y-2">
                  {interview.linked_tracks.map((track) => (
                    <div key={track.track_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                          <Music className="w-5 h-5 text-primary" />
                        </div>
                        <span className="font-medium">{track.title}</span>
                      </div>
                      {track.spotify_url && (
                        <a href={track.spotify_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="text-green-400 border-green-400/30">
                            <ExternalLink className="w-3 h-3 mr-1" /> Spotify
                          </Button>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card className="glass-card border-border p-4">
              <div className="flex gap-2">
                <Button className="flex-1" variant="outline">
                  <Heart className="w-4 h-4 mr-2" /> Like
                </Button>
                <Button className="flex-1" variant="outline">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              </div>
            </Card>

            {/* External Links */}
            {(interview.youtube_url || interview.vimeo_url) && (
              <Card className="glass-card border-border p-4">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Watch On</h3>
                <div className="space-y-2">
                  {interview.youtube_url && (
                    <a href={interview.youtube_url} target="_blank" rel="noopener noreferrer" className="block">
                      <Button variant="outline" className="w-full justify-start text-red-400 border-red-400/30">
                        <ExternalLink className="w-4 h-4 mr-2" /> YouTube
                      </Button>
                    </a>
                  )}
                  {interview.vimeo_url && (
                    <a href={interview.vimeo_url} target="_blank" rel="noopener noreferrer" className="block">
                      <Button variant="outline" className="w-full justify-start text-blue-400 border-blue-400/30">
                        <ExternalLink className="w-4 h-4 mr-2" /> Vimeo
                      </Button>
                    </a>
                  )}
                </div>
              </Card>
            )}

            {/* Credits */}
            {(interview.shot_by || interview.engineered_by || interview.recorded_at) && (
              <Card className="glass-card border-border p-4">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Credits</h3>
                <div className="space-y-3">
                  {interview.shot_by && (
                    <div className="flex items-center gap-2 text-sm">
                      <Camera className="w-4 h-4 text-purple-400" />
                      <span className="text-muted-foreground">Shot by</span>
                      <span className="font-medium">{interview.shot_by.name}</span>
                    </div>
                  )}
                  {interview.engineered_by && (
                    <div className="flex items-center gap-2 text-sm">
                      <Headphones className="w-4 h-4 text-blue-400" />
                      <span className="text-muted-foreground">Engineered by</span>
                      <span className="font-medium">{interview.engineered_by.name}</span>
                    </div>
                  )}
                  {interview.recorded_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-amber-400" />
                      <span className="text-muted-foreground">Recorded at</span>
                      <span className="font-medium">{interview.recorded_at.name}</span>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
